import {useCallback} from 'react';
import {safeCaptureException} from '../../../utils/sentry';
import {useFaceModelsStore} from '../store';
import {IS_DEV_MODE} from '../../../utils/devMode';
import {importWithRetry} from '@chekin/core';

type ModelConfig = {
  runtime: 'mediapipe';
  solutionPath: string;
  refineLandmarks: boolean;
};

const MEDIAPIPE_CDN_PATH = 'https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh';

export interface UsePreloadFaceModelsProps {
  shouldPreload?: boolean;
}

export const usePreloadFaceModels = ({
  shouldPreload = true,
}: UsePreloadFaceModelsProps = {}) => {
  const detector = useFaceModelsStore(state => state.detector);
  const isLoading = useFaceModelsStore(state => state.isLoading);
  const setDetector = useFaceModelsStore(state => state.setDetector);
  const setIsLoading = useFaceModelsStore(state => state.setIsLoading);
  const hasFailed = useFaceModelsStore(state => state.hasFailed);
  const setHasFailed = useFaceModelsStore(state => state.setHasFailed);

  const isLoaded = Boolean(detector);

  const preload = useCallback(async () => {
    if (!shouldPreload) {
      return;
    }

    if (isLoading || isLoaded || hasFailed) return;
    setIsLoading(true);

    try {
      if (IS_DEV_MODE) console.log('Preloading face detection models...');

      const tf = await importWithRetry(() => import('@tensorflow/tfjs'), {
        isExpectedModule: module => typeof module.ready === 'function',
      });

      let backendName = 'webgl';
      let backendSupported = false;

      try {
        await tf.ready();
        backendSupported = await tf
          .setBackend('webgl')
          .then(() => true)
          .catch(() => false);

        if (backendSupported) {
          if (IS_DEV_MODE) console.log('Using WebGL backend for face detection');
        }
      } catch (error) {
        safeCaptureException(error, 'Face detection WebGL backend');
        console.warn('WebGL backend failed:', error);
        backendSupported = false;
      }

      if (!backendSupported) {
        try {
          if (IS_DEV_MODE)
            console.log('WebGL not supported, falling back to CPU backend...');
          backendName = 'cpu';
          await tf.setBackend('cpu');
          backendSupported = true;
          if (IS_DEV_MODE) console.log('Using CPU backend for face detection');
        } catch (error) {
          safeCaptureException(error, 'Face detection CPU backend');
          console.error('CPU backend also failed:', error);

          try {
            if (IS_DEV_MODE) console.log('CPU backend failed, trying WASM backend...');
            backendName = 'wasm';
            await tf.setBackend('wasm');
            backendSupported = true;
            if (IS_DEV_MODE) console.log('Using WASM backend for face detection');
          } catch (wasmError) {
            safeCaptureException(wasmError, 'Face detection WASM backend');
            console.error('All backends failed:', wasmError);
            setHasFailed(true);
            setIsLoading(false);
            return;
          }
        }
      }

      const faceLandmarksModule = await importWithRetry(
        () => import('@tensorflow-models/face-landmarks-detection'),
        {isExpectedModule: module => typeof module.createDetector === 'function'},
      );
      const {SupportedModels, createDetector} = faceLandmarksModule;

      const model = SupportedModels.MediaPipeFaceMesh;
      const modelConfig: ModelConfig = {
        runtime: 'mediapipe',
        solutionPath: MEDIAPIPE_CDN_PATH,
        refineLandmarks: true,
      };

      const faceDetector = await createDetector(model, modelConfig);

      setDetector(faceDetector);
      setIsLoading(false);
      if (IS_DEV_MODE)
        console.log(
          `Face detection models preloaded successfully using ${backendName} backend`,
        );
    } catch (err) {
      safeCaptureException(err, 'Preloading face detection models');
      console.error('Failed to preload face detection models:', err);
      setHasFailed(true);
      setIsLoading(false);
    }
  }, [
    isLoading,
    isLoaded,
    hasFailed,
    setIsLoading,
    setHasFailed,
    setDetector,
    shouldPreload,
  ]);

  return {
    isLoading,
    detector,
    isLoaded,
    hasFailed,
    preload,
  };
};
